import {
  useEffect,
  useRef,
  useCallback,
  ChangeEventHandler,
  ChangeEvent,
  forwardRef,
  useState,
} from 'react';

import { InputDescriptionStyled } from '../styles/inputs.style';
import * as Styled from './auto-resizing-textarea.style';

type Props = {
  id?: string;
  name?: string;
  rows?: number;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  initialValue?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  maxLength?: number;
  showCounter?: boolean;
  hasError?: boolean;
};

const AutoResizingTextArea = forwardRef(
  (
    {
      id,
      name,
      rows,
      initialValue,
      placeholder,
      disabled,
      className,
      maxLength,
      showCounter,
      hasError,
      onChange,
      ...rest
    }: Props,
    ref
  ) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

    const updateFieldHeight = useCallback(() => {
      if (textAreaRef.current) {
        textAreaRef.current.style.height = 'auto';
        textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
      }
    }, []);

    const updateHeight = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement>) => {
        updateFieldHeight();
        onChange && onChange(event);
      },
      [onChange, updateFieldHeight]
    );

    useEffect(() => {
      if (!isEditing && textAreaRef.current) {
        textAreaRef.current.value = initialValue || '';
      }
      updateFieldHeight();
    }, [initialValue, isEditing, updateFieldHeight]);

    useEffect(() => {
      window.addEventListener('resize', updateFieldHeight, false);
      return () => {
        window.removeEventListener('resize', updateFieldHeight);
      };
    });

    return (
      <>
        <Styled.TextArea
          id={id}
          name={name}
          rows={rows ? rows : 1}
          ref={el => {
            if (typeof ref === 'function') ref(el);
            textAreaRef.current = el;
          }}
          defaultValue={initialValue}
          onFocus={() => setIsEditing(true)}
          onBlur={() => setIsEditing(false)}
          onChange={updateHeight}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
          maxLength={maxLength}
          $hasError={hasError}
          {...rest}
        />
        {showCounter && !hasError && (
          <InputDescriptionStyled textAlign="right">
            {initialValue ? initialValue.length : 0}/{maxLength}
          </InputDescriptionStyled>
        )}
      </>
    );
  }
);

export default AutoResizingTextArea;
